"use client";
import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import ArrowLeft from "@/components/icons/arrow-left";
import PayBill from "@/components/icons/pay-bill";
import NewConnection from "@/components/icons/new-connection";
import Complaints from "@/components/icons/complaints";
import { motion } from "framer-motion";
import KnowBill from "@/components/icons/know-bill";
import Button from "@/components/form/button";
import Aeroplane from "@/components/icons/aeroplane";

const Carousel = ({
  images,
  pageIcons = [],
  autoPlay = false,
  interval = 14000,
  showSidebar = true,
  showDots = true,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [isHovered, setIsHovered] = useState(false);

  const iconMap = {
    PayBill: <PayBill className="w-6 h-6" />,
    NewConnection: <NewConnection className="w-6 h-6" />,
    Complaints: <Complaints className="w-6 h-6" />,
    KnowBill: <KnowBill className="w-6 h-6" />,
  };

  const goToPrevious = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  useEffect(() => {
    if (!autoPlay || isHovered) return;

    const timer = setInterval(goToNext, interval);
    return () => clearInterval(timer);
  }, [currentImageIndex, autoPlay, interval, isHovered]);

  return (
    <section
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative w-full h-[240px] sm:h-[250px] md:h-[300px] lg:h-[380px] 2xl:h-[500px] bg-cover"
    >
      {/* Slides */}
      {images.map((image, index) => (
        <div
          key={index}
          className={`w-full h-full absolute transition-opacity duration-500 ${index === currentImageIndex
            ? "opacity-100 z-10"
            : "opacity-0 z-0 pointer-events-none"
            }`}
        >
          <Image
            fill
            priority={index === 0}
            src={image.src}
            alt={image.alt}
            className="relative aspect-[600/1920] w-full"
            sizes="(max-width: 640px) 50vw, (max-width: 768px) 60vw, (max-width: 1280px) 90vw, 90vw"
          />
          {/* Text overlay */}
          {index === currentImageIndex && (
            <div
              className={`absolute left-[5%] w-[50%] lg:w-[40%]
            ${index === 3
                  ? "top-[10%] sm:top-[30%]"
                  : index === 4
                    ? "flex top-[10%] sm:top-[40%] lg:top-[50%] 2xl:top-[60%] w-[80%] lg:w-[80%]"
                    : index === 8
                      ? "top-[20%]"
                      : index === 9
                        ? "top-[10%] lg:top-[30%]"
                        : index === 5 || index === 6
                          ? "top-[10%] sm:top-[40%] lg:top-[48%] xl:top-[50%] lg:left-[18%]  lg:w-[50%] text-center"
                          : index === 7
                            ? "flex items-center w-[90%] lg:w-[90%] xl:w-[95%] 2xl:w-[100%] top-[25%] sm:top-[40%] lg:top-[45%] 2xl:top-[55%]"
                            : index === 0
                              ? "top-[10%]"
                              : "top-[30%]"
                }`}
            >
              <div>
                <div
                  className={` ${index === 3 || index === 4 || index === 5 || index === 6
                    ? "bg-black bg-opacity-30 rounded-xl py-1 px-0.5 w-fit"
                    : null
                    }`}
                >
                  {image.subTitle && (
                    <section
                      className={`${index === 4 ? "bg-white" : "bg-green-bg"
                        } rounded-xl border border-green-text px-1 sm:px-3 sm:py-1 w-fit mb-4`}
                    >
                      <p
                        className={` text-[9px] sm:text-base text-green-text font-bold`}
                      >
                        {image.subTitle}
                      </p>
                    </section>
                  )}
                  <h3
                    className={`text-gray-800 text-xs md:text-xl lg:text-lg xl:text-2xl 2xl:text-3xl font-medium 
                ${index === 3 || index === 4 || index === 5 || index === 6
                        ? "text-white lg:text-lg xl:text-2xl"
                        : index === 7
                          ? "text-white bg-black bg-opacity-25 px-2 py-1 rounded-lg "
                          : index === 8
                            ? "text-green-2"
                            : null
                      }`}
                    dangerouslySetInnerHTML={{ __html: image.title }}
                  />

                  {image.subTitle2 && (
                    <p
                      className={`${index === 7 ? "text-gray-700 italic font-bold md:text-xl" : "text-white"
                        } text-xs lg:text-lg font-medium mt-2`}
                    >
                      {image.subTitle2}
                    </p>
                  )}
                </div>
                <div className="w-fit">
                  {image.button && (
                    <Button
                      aria-label={image.button}
                      target={index === 2 ? "_blank" : null}
                      href={image.url}
                      text={image.button}
                      icon={<Aeroplane className="w-5" />}
                      className="px-0.5 lg:px-1 2xl:px-3 text-xs lg:text-base"
                    />
                  )}
                </div>
                {image.moneyIcons && (
                  <div className="flex">
                    {image.moneyIcons.map((icon, i) => (
                      <div key={i} className="mr-4 mt-1 sm:mt-4">
                        <div
                          className={`bg-white h-9 lg:h-20 w-full rounded-lg p-2.5 cursor-pointer border border-red-400 flex justify-center items-center`}
                        >
                          <Image
                            className={
                              i === 1
                                ? "h-6 w-12 lg:h-8 lg:w-16 lg:mt-0"
                                : i === 3
                                  ? "h-6 w-12 lg:h-6 lg:w-16 lg:mt-0"
                                  : "h-6 w-6 lg:h-16 lg:w-16"
                            }
                            src={icon.src}
                            alt={icon.title}
                            width={100}
                            height={100}
                            unoptimized
                            sizes="(max-width: 640px) 50px, (max-width: 768px) 60px, (max-width: 1280px) 100px, 100px"
                          />
                        </div>
                        <p className="mt-2 text-xs sm:text-sm font-medium ml-0.5">
                          {icon.title}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="ml-10 w-[700px]">
                {image.counts && (
                  <div className="bg-white opacity-75 w-fit rounded-lg px-2 py-1 mt-4">
                    {image.counts.map((count, i) => (
                      <div
                        key={i}
                        // className="bg-white h-fit w-48 md:w-24 xl:w-[20%] m-0.5 md:m-2 p-1 rounded-lg text-center mt-1 sm:mt-5"
                        className="flex justify-between"
                      >
                        <p className="text-xs lg:text-sm xl:text-base font-bold">
                          {count.title}
                        </p>
                        <p className="text-xs md:text-sm lg:text-lg 2xl:text-lg font-bold ml-2">
                          {count.count}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Sidebar */}
      {showSidebar && pageIcons.length > 0 && (
        <motion.div
          initial={{ y: -200, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1, duration: 1, ease: "easeInOut" }}
          className="absolute right-0 lg:top-2 2xl:top-10 hidden lg:flex cursor-pointer flex-col items-end z-20"
        >
          {pageIcons.map((item, i) => (
            <Link
              href={item.url}
              target={i === 2 ? null : "_blank"}
              key={i}
              className="relative group bg-[#004d40] bg-opacity-70 hover:bg-green-11 mb-2 md:py-6 lg:py-12 md:my-1 lg:my-2 h-[82px] w-32 border border-gray-300 border-r-0 rounded-l-lg flex flex-col items-center justify-center"
              tabIndex={currentImageIndex === 0 ? 0 : -1}
            >
              <section className="bg-white rounded-full h-10 w-10 p-2.5 flex justify-center items-center">
                {iconMap[item.icon]}
              </section>
              <span className="text-white text-sm">{item.title}</span>
            </Link>
          ))}
        </motion.div>
      )}

      {/* Navigation Dots - Increased z-index */}
      {showDots && (
        <motion.div
          initial={{ y: -200, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1, duration: 1, ease: "easeInOut" }}
          className="absolute bottom-0 sm:bottom-2 xl:bottom-4 flex justify-center space-x-2 z-20 w-full"
        >
          <button
            onClick={goToPrevious}
            aria-label="Previous Image"
            className="hover:bg-green-active hover:text-white hover:duration-700 rounded-full p-1"
          >
            <ArrowLeft stroke="2.5" className="h-6 w-6 xl:h-7 xl:w-7" />
          </button>

          {images.map((_, index) => (
            <div
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-3.5 h-3.5 2xl:w-4 2xl:h-4 mt-2 rounded-full cursor-pointer ${currentImageIndex === index ? "bg-green-active" : "bg-green-low"
                }`}
            />
          ))}

          <button
            onClick={goToNext}
            aria-label="Next Image"
            className="hover:bg-green-active hover:text-white hover:duration-700 rounded-full p-1"
          >
            <ArrowLeft
              stroke="2.5"
              className="h-6 w-6 xl:h-7 xl:w-7 rotate-180"
            />
          </button>
        </motion.div>
      )}
    </section>
  );
};

export default Carousel;


// "use client";
// import { useState, useEffect } from "react";
// import Image from "next/image";
// import Link from "next/link";
// import ArrowLeft from "@/components/icons/arrow-left";
// import PayBill from "@/components/icons/pay-bill";
// import NewConnection from "@/components/icons/new-connection";
// import Complaints from "@/components/icons/complaints";
// import { motion } from "framer-motion";
// import KnowBill from "@/components/icons/know-bill";
// import Button from "@/components/form/button";
// import Aeroplane from "@/components/icons/aeroplane";

// const Carousel = ({
//   images,
//   pageIcons = [],
//   autoPlay = false,
//   interval = 14000,
//   showSidebar = true,
//   showDots = true,
// }) => {
//   const [currentImageIndex, setCurrentImageIndex] = useState(0);

//   const [isHovered, setIsHovered] = useState(false);

//   const iconMap = {
//     PayBill: <PayBill className="w-6 h-6" />,
//     NewConnection: <NewConnection className="w-6 h-6" />,
//     Complaints: <Complaints className="w-6 h-6" />,
//     KnowBill: <KnowBill className="w-6 h-6" />,
//   };

//   const goToPrevious = () => {
//     setCurrentImageIndex((prevIndex) =>
//       prevIndex === 0 ? images.length - 1 : prevIndex - 1
//     );
//   };

//   const goToNext = () => {
//     setCurrentImageIndex((prevIndex) =>
//       prevIndex === images.length - 1 ? 0 : prevIndex + 1
//     );
//   };

//   useEffect(() => {
//     if (!autoPlay || isHovered) return;

//     const timer = setInterval(goToNext, interval);
//     return () => clearInterval(timer);
//   }, [currentImageIndex, autoPlay, interval, isHovered]);

//   return (
//     <section
//       onMouseEnter={() => setIsHovered(true)}
//       onMouseLeave={() => setIsHovered(false)}
//       className="relative w-full h-[240px] sm:h-[250px] md:h-[300px] lg:h-[380px] 2xl:h-[500px] bg-cover"
//     >
//       {/* Slides */}
//       {images.map((image, index) => (
//         <div
//           key={index}
//           className={`w-full h-full absolute transition-opacity duration-500 ${index === currentImageIndex
//             ? "opacity-100 z-10"
//             : "opacity-0 z-0 pointer-events-none"
//             }`}
//         >
//           <Image
//             fill
//             priority={index === 0}
//             src={image.src}
//             alt={image.alt}
//             className="relative aspect-[600/1920] w-full"
//             sizes="(max-width: 640px) 50vw, (max-width: 768px) 60vw, (max-width: 1280px) 90vw, 90vw"
//           />
//           {/* Text overlay */}
//           {index === currentImageIndex && (
//             <div
//               className={`absolute left-[5%] w-[50%] lg:w-[40%]
//             ${index === 2
//                   ? "top-[10%] sm:top-[30%]"
//                   : index === 3
//                     ? "flex top-[10%] sm:top-[40%] lg:top-[50%] 2xl:top-[60%] w-[80%] lg:w-[80%]"
//                     : index === 7
//                       ? "top-[20%]"
//                       : index === 8
//                         ? "top-[10%] lg:top-[30%]"
//                         : index === 4 || index === 5
//                           ? "top-[10%] sm:top-[40%] lg:top-[48%] xl:top-[50%] lg:left-[18%]  lg:w-[50%] text-center"
//                           : index === 6
//                             ? "flex items-center w-[90%] lg:w-[90%] xl:w-[95%] 2xl:w-[100%] top-[25%] sm:top-[40%] lg:top-[45%] 2xl:top-[55%]"
//                             : "top-[30%]"
//                 }`}
//             >
//               <div>
//                 <div
//                   className={` ${index === 2 || index === 3 || index === 4 || index === 5
//                     ? "bg-black bg-opacity-30 rounded-xl py-1 px-0.5 w-fit"
//                     : null
//                     }`}
//                 >
//                   {image.subTitle && (
//                     <section
//                       className={`${index === 3 ? "bg-white" : "bg-green-bg"
//                         } rounded-xl border border-green-text px-1 sm:px-3 sm:py-1 w-fit mb-4`}
//                     >
//                       <p
//                         className={` text-[9px] sm:text-base text-green-text font-bold`}
//                       >
//                         {image.subTitle}
//                       </p>
//                     </section>
//                   )}
//                   <h3
//                     className={`text-gray-800 text-xs md:text-xl lg:text-lg xl:text-2xl 2xl:text-3xl font-medium
//                 ${index === 2 || index === 3 || index === 4 || index === 5
//                         ? "text-white lg:text-lg xl:text-2xl"
//                         : index === 6
//                           ? "text-white bg-black bg-opacity-25 px-2 py-1 rounded-lg "
//                           : index === 7
//                             ? "text-green-2"
//                             : null
//                       }`}
//                     dangerouslySetInnerHTML={{ __html: image.title }}
//                   />

//                   {image.subTitle2 && (
//                     <p
//                       className={`${index === 6 ? "text-gray-700 italic font-bold md:text-xl" : "text-white"
//                         } text-xs lg:text-lg font-medium mt-2`}
//                     >
//                       {image.subTitle2}
//                     </p>
//                   )}
//                 </div>
//                 <div className="w-fit">
//                   {image.button && (
//                     <Button
//                       aria-label={image.button}
//                       target={index === 2 ? "_blank" : null}
//                       href={image.url}
//                       text={image.button}
//                       icon={<Aeroplane className="w-5" />}
//                       className="px-0.5 lg:px-1 2xl:px-3 text-xs lg:text-base"
//                     />
//                   )}
//                 </div>
//                 {image.moneyIcons && (
//                   <div className="flex">
//                     {image.moneyIcons.map((icon, i) => (
//                       <div key={i} className="mr-4 mt-1 sm:mt-4">
//                         <div
//                           className={`bg-white h-9 lg:h-20 w-full rounded-lg p-2.5 cursor-pointer border border-red-400 flex justify-center items-center`}
//                         >
//                           <Image
//                             className={
//                               i === 1
//                                 ? "h-6 w-12 lg:h-8 lg:w-16 lg:mt-0"
//                                 : i === 3
//                                   ? "h-6 w-12 lg:h-6 lg:w-16 lg:mt-0"
//                                   : "h-6 w-6 lg:h-16 lg:w-16"
//                             }
//                             src={icon.src}
//                             alt={icon.title}
//                             width={100}
//                             height={100}
//                             unoptimized
//                             sizes="(max-width: 640px) 50px, (max-width: 768px) 60px, (max-width: 1280px) 100px, 100px"
//                           />
//                         </div>
//                         <p className="mt-2 text-xs sm:text-sm font-medium ml-0.5">
//                           {icon.title}
//                         </p>
//                       </div>
//                     ))}
//                   </div>
//                 )}
//               </div>
//               <div className="ml-10 w-[700px]">
//                 {image.counts && (
//                   <div className="bg-white opacity-75 w-fit rounded-lg px-2 py-1 mt-4">
//                     {image.counts.map((count, i) => (
//                       <div
//                         key={i}
//                         // className="bg-white h-fit w-48 md:w-24 xl:w-[20%] m-0.5 md:m-2 p-1 rounded-lg text-center mt-1 sm:mt-5"
//                         className="flex justify-between"
//                       >
//                         <p className="text-xs lg:text-sm xl:text-base font-bold">
//                           {count.title}
//                         </p>
//                         <p className="text-xs md:text-sm lg:text-lg 2xl:text-lg font-bold ml-2">
//                           {count.count}
//                         </p>
//                       </div>
//                     ))}
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}
//         </div>
//       ))}

//       {/* Sidebar */}
//       {showSidebar && pageIcons.length > 0 && (
//         <motion.div
//           initial={{ y: -200, opacity: 0 }}
//           animate={{ y: 0, opacity: 1 }}
//           transition={{ delay: 1, duration: 1, ease: "easeInOut" }}
//           className="absolute right-0 lg:top-2 2xl:top-10 hidden lg:flex cursor-pointer flex-col items-end z-20"
//         >
//           {pageIcons.map((item, i) => (
//             <Link
//               href={item.url}
//               target={i === 2 ? 'null' : "_blank"}
//               key={i}
//               className="relative group bg-[#004d40] bg-opacity-70 hover:bg-green-11 mb-2 md:py-6 lg:py-12 md:my-1 lg:my-2 h-[82px] w-32 border border-gray-300 border-r-0 rounded-l-lg flex flex-col items-center justify-center"
//               tabIndex={currentImageIndex === 0 ? 0 : -1}
//             >
//               <section className="bg-white rounded-full h-10 w-10 p-2.5 flex justify-center items-center">
//                 {iconMap[item.icon]}
//               </section>
//               <span className="text-white text-sm">{item.title}</span>
//             </Link>
//           ))}
//         </motion.div>
//       )}

//       {/* Navigation Dots - Increased z-index */}
//       {showDots && (
//         <motion.div
//           initial={{ y: -200, opacity: 0 }}
//           animate={{ y: 0, opacity: 1 }}
//           transition={{ delay: 1, duration: 1, ease: "easeInOut" }}
//           className="absolute bottom-0 sm:bottom-2 xl:bottom-4 flex justify-center space-x-2 z-20 w-full"
//         >
//           <button
//             onClick={goToPrevious}
//             aria-label="Previous Image"
//             className="hover:bg-green-active hover:text-white hover:duration-700 rounded-full p-1"
//           >
//             <ArrowLeft stroke="2.5" className="h-6 w-6 xl:h-7 xl:w-7" />
//           </button>

//           {images.map((_, index) => (
//             <div
//               key={index}
//               onClick={() => setCurrentImageIndex(index)}
//               className={`w-3.5 h-3.5 2xl:w-4 2xl:h-4 mt-2 rounded-full cursor-pointer ${currentImageIndex === index ? "bg-green-active" : "bg-green-low"
//                 }`}
//             />
//           ))}

//           <button
//             onClick={goToNext}
//             aria-label="Next Image"
//             className="hover:bg-green-active hover:text-white hover:duration-700 rounded-full p-1"
//           >
//             <ArrowLeft
//               stroke="2.5"
//               className="h-6 w-6 xl:h-7 xl:w-7 rotate-180"
//             />
//           </button>
//         </motion.div>
//       )}
//     </section>
//   );
// };

// export default Carousel;